import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, CustomSelect, RichTextEditor, Toggle } from '@/components/ui';
import Input from '@/components/ui/Input/Input';
import FileUpload from '@/components/ui/FileUpload';
import { Heading, Paragraph } from '@/components/ui/Typography';
import { ProgrammeFormBaseSchema } from '@/lib/api/types';
import TemplatePreviewCard from '@/pages/articles-pages/programmes/MultiStepForm/MicroSite/TemplatePreviewCard';
import micrositeTemplatesData from '@/pages/articles-pages/programmes/MultiStepForm/MicroSite/micrositeTemplateTypesData';
import { Image, Trash2Icon } from 'lucide-react';
// import { ProgrammeFormSchema } from '@/lib/api/types';
import { useParams } from 'react-router-dom';
import { IProgrammeMultiStepFormValues } from '@/pages/articles-pages/programmes/MultiStepForm/MultiStepForm';

export interface MainFormStageResult {
  values: IProgrammeMultiStepFormValues;
  coverImageFile: File | null;
  coverImagePreviewUrl: string;
}

interface IMainFormProps  {
 nextStep: () => void;
 prevStep: () => void;
 goToPage: (page: number) => void;
 formValues: IProgrammeMultiStepFormValues;
 handleFormData: (data: IProgrammeMultiStepFormValues) => void;
}

const websiteTypeOptions = [
  { value: 'WEBSITE_LINK', label: 'Add a website link' },
  { value: 'MICROSITE', label: 'Use a microsite' },
];

const MainFormSchema = ProgrammeFormBaseSchema.omit({
  microSiteRequest: true,
  localCoverImageFile: true,
});

type MainFormValues = z.input<typeof MainFormSchema>;

const MainForm = ({
  nextStep,
  prevStep,
  goToPage,
  formValues,
  handleFormData,
}: IMainFormProps) => {

    const { id } = useParams<{ id?: string }>();
    const isEdit = Boolean(id && location.pathname.includes('/edit'));

    const urlQueryParams = new URLSearchParams(window.location.search);

  const [localCoverImageFile, setLocalCoverImageFile] = useState<File | null>(formValues.localCoverImageFile || null);
  const [localCoverImagePreviewUrl, setLocalCoverImagePreviewUrl] = useState(formValues.localCoverImageFile ? URL.createObjectURL(formValues.localCoverImageFile) : formValues.coverImageUrl || '');
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MainFormValues>({
    resolver: zodResolver(MainFormSchema),
    defaultValues: {
      ...formValues
    },
    mode: 'onChange',
  });

  useEffect(() => {
    reset({
      ...formValues
    });
    setLocalCoverImageFile(formValues.localCoverImageFile || null);
    setLocalCoverImagePreviewUrl(formValues.localCoverImageFile ? URL.createObjectURL(formValues.localCoverImageFile) : '');
  }, [formValues, reset]);

  const getErrorMessage = (value: unknown) => (typeof value === 'string' ? value : undefined);

  const isAddWebsite = Boolean(watch('isAddWebsite'));
  const websiteType = String(watch('websiteType') ?? '');
  const shouldGoToMicrosite = isAddWebsite && websiteType === 'MICROSITE';

  // const parseTagString = (value: string): string[] => value
  // .split(',')
  // .map((tag) => tag.trim())
  // .filter(Boolean);

  const handleCoverImageChange = (files: File[] | null) => {
    if (!files?.length) return;
    const file = files[0];
    const nextPreviewUrl = URL.createObjectURL(file);

    if (localCoverImagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(localCoverImagePreviewUrl);
    }

    setLocalCoverImageFile(file);
    setLocalCoverImagePreviewUrl(nextPreviewUrl);
  };

  const handleClearCoverImage = () => {
    if (localCoverImagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(localCoverImagePreviewUrl);
    }
    setLocalCoverImageFile(null);
    setLocalCoverImagePreviewUrl('');
    setValue('coverImageUrl', '', { shouldDirty: true, shouldValidate: true });
  };

  const handleContinue = (values: MainFormValues) => {
    handleFormData({
      ...formValues,
      ...values,
      localCoverImageFile,
      coverImageUrl: localCoverImagePreviewUrl || values.coverImageUrl || '',
    });

    if (shouldGoToMicrosite) {
      if(isEdit) {
        goToPage(3);
        urlQueryParams.set('action', 'edit');
        window.history.replaceState(null, '', `${window.location.pathname}?${urlQueryParams.toString()}`);
      } else {
        nextStep();
           urlQueryParams.set('action', 'edit');
        window.history.replaceState(null, '', `${window.location.pathname}?${urlQueryParams.toString()}`);
      }
    } else {
      goToPage(4);
    }
  };

  return (
    <div className="flex flex-col gap-6 rounded-xl bg-white p-6">
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <Heading level={4}>{isEdit ? 'Edit Programme' : 'Create Programme'}</Heading>
      
      </div>

      <Controller
        name="title"
        control={control}
        render={({ field }) => (
          <Input
            label="Title"
            required
            value={String(field.value ?? '')}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder="Enter programme title"
            error={getErrorMessage(errors.title?.message)}
          />
        )}
      />

      <div className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <Paragraph className="text-sm font-semibold">Cover Image</Paragraph>
          {localCoverImagePreviewUrl ? (
            <Button className="flex gap-2 items-center bg-red-500 text-white" onClick={handleClearCoverImage}>
              <Trash2Icon className="w-4 h-4" />
              Remove image
            </Button>
          ) : null}
        </div>

<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
  <div className="flex flex-col gap-4 lg:gap-6 h-full">
      <FileUpload
          onChange={handleCoverImageChange}
          showPreview={false}
          multiple={false}
          allowedFileTypes={['image/*']}
          $maxSizeMB={5}
          containerClassName="border border-dashed h-full flex-1 border-gray-300 bg-gray-50"
          componentClassName="h-full"
          placeholder=""
        >
          <div className="flex flex-col h-full items-center justify-center gap-2 py-6 text-center">
            <Image className="" strokeWidth={1.25} size={40} />
            <Paragraph className="!text-sm font-semibold">Click or drag image here</Paragraph>
            <Paragraph className="!text-sm">PNG, JPEG, WEBP</Paragraph>
            <Paragraph className="!text-sm">(max 5MB)</Paragraph>
          </div>
        </FileUpload>
     <Controller
        name="coverImageTitle"
        control={control}
        render={({ field }) => (
          <Input
          {...field}
            label="Cover Image Caption"
            $marginbottom={"0"}
            placeholder="Describe this image for accessibility"
            error={getErrorMessage(errors.coverImageTitle?.message)}
          />
        )}
      /> 
  </div>


        <div className="flex flex-col gap-4 h-full">
          {localCoverImagePreviewUrl ? (
          <div className="overflow-hidden rounded-md border border-gray-100">
            <img src={localCoverImagePreviewUrl} alt="Cover preview" className="h-full w-full object-cover" />
          </div>
        ) : formValues.coverImageUrl ? <div className="h-full flex items-center justify-center text-sm w-full bg-gray-100 rounded-md">
            <img src={formValues.coverImageUrl}  className="h-full w-full object-cover" />
        </div> : <div className="h-full flex items-center justify-center text-sm w-full bg-gray-100 rounded-md">
            <Paragraph className="text-gray-500">No cover image uploaded</Paragraph>
          </div>}
           
        </div>
   
</div>
        

        
      </div>

      <div className="rounded-lg border border-gray-200 p-4 pb-6 flex flex-col gap-2">
         <Paragraph className="text-sm font-semibold after:content-['*'] after:ml-0.5 after:text-red-500 ">Content</Paragraph>
       
 <Controller
        name="content"
        control={control}
        render={({ field }) => (
          <RichTextEditor
            label=""
            required
            value={String(field.value ?? '')}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={getErrorMessage(errors.content?.message)}
          />
        )}
      />
      </div>

     

           <Controller
                     name="articleTag"
                     control={control}
                     render={({ field }) => (
                       <div>
                         <CustomSelect
                         {...field}
                         value={field.value ?? ''}
                         label="Tags"
                           onChange={(selectedValues) => {
                             const values = Array.isArray(selectedValues) ? selectedValues : [];
                             field.onChange(values.join(', '));
                           }}
                           options={[]}
                           placeholder={'Create tags'}
                           isMulti
                           isSearchable
                           isClearable
                           isCreatable
                           multiValueLayout='scroll'
                         />
                       </div>
                     )}
                   />

      <Controller
        name="allowComments"
        control={control}
        render={({ field }) => (
          <Toggle
            checked={Boolean(field.value)}
            onChange={field.onChange}
            label="Allow comments"
            description="Disable to hide comment section for this programme"
          />
        )}
      />

      <Controller
        name="isAddWebsite"
        control={control}
        render={({ field }) => (
          <Toggle
            checked={Boolean(field.value)}
            onChange={(checked) => {
              field.onChange(checked);
              if (!checked) {
                setValue('websiteType', '', { shouldDirty: true, shouldValidate: true });
                setValue('websiteLink', '', { shouldDirty: true, shouldValidate: true });
                return;
              }

              if (!watch('websiteType')) {
                setValue('websiteType', 'WEBSITE_LINK', { shouldDirty: true, shouldValidate: true });
              }
            }}
            label="Add a website"
            description="Attach an external website or microsite to this programme"
          />
        )}
      />

      {isAddWebsite ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 items-end">
          <Controller
            name="websiteType"
            control={control}
            render={({ field }) => (
              <CustomSelect
                id="websiteType"
                label="Website Type"
                required
                value={String(field.value ?? '')}
                onChange={(value) => {
                  field.onChange(value as string);
                  if (value === 'MICROSITE') {
                    setValue('websiteLink', '', { shouldDirty: true, shouldValidate: true });
                  }
                }}
                onBlur={field.onBlur}
                options={websiteTypeOptions}
                placeholder="Select website type"
                error={getErrorMessage(errors.websiteType?.message)}
              />
            )}
          />

          {websiteType === 'WEBSITE_LINK' ? (
            <Controller
              name="websiteLink"
              control={control}
              render={({ field }) => (
                <Input
                  label="Website Link"
                  value={String(field.value ?? '')}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="https://example.com"
                  error={getErrorMessage(errors.websiteLink?.message)}
                  $marginbottom={"0"}
                />
              )}
            />
          ) : null}
        </div>
      ) : null}

      {isAddWebsite && websiteType === 'MICROSITE' && isEdit  ? (
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <Paragraph className="text-sm font-semibold text-gray-700">Microsite Template</Paragraph>
              {/* <Paragraph className="text-xs text-gray-500">
                Select and customize a microsite before publishing.
              </Paragraph> */}
            </div>
            {/* <Button type="button" onClick={() => goToPage(2)}>
              {selectedMicrositeTemplateType ? 'Edit microsite' : 'Select microsite'}
            </Button> */}
          </div>

            <div className="mt-4 md:w-1/3">
              <TemplatePreviewCard
              readOnly
              
                template={micrositeTemplatesData.find((item) => item.id === formValues.microSiteRequest?.micrositeTemplateType) ?? micrositeTemplatesData[0]}
              />
            </div>
       
        </div>
      ) : null}

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <Button type="button" variant="secondary" onClick={prevStep}>
          Go back
        </Button>
        <Button type="button" onClick={handleSubmit((values) => handleContinue(values))}>
          {shouldGoToMicrosite ? 'Continue to Microsite' : 'Proceed to Review'}
        </Button>
      </div>
    </div>
  );
};

export default MainForm;